/**
 * AdminSettingsProvider — single source of truth for the admin portal's
 * personalization payload.
 *
 * Persistence layers:
 *  - in-memory React state (instant reads)
 *  - localStorage (survives reloads; primes the UI before the API call)
 *  - backend via adminSettingsService (authoritative; debounced writes)
 *
 * Context object, defaults and the `useAdminSettings` hook live in the
 * sibling `admin-settings.context.ts` so this file can stay components-only
 * and play nicely with Vite's React Fast Refresh.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  adminSettingsService,
  type AdminSettingsCategory,
  type AdminSettingsPayload,
} from '@/src/modules/services/admin-settings.service';
import { useAuth } from '@/src/modules/auth/useAuth';
import {
  DEFAULT_SETTINGS,
  LOCAL_KEY,
  SettingsContext,
  type SettingsContextValue,
} from './admin-settings.context';

function readCache(): AdminSettingsPayload | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return null;
  }
}

function writeCache(payload: AdminSettingsPayload) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be full or disabled; non-fatal.
  }
}

export function AdminSettingsProvider({ children }: { children: ReactNode }) {
  const { isAdmin, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState<AdminSettingsPayload>(
    () => readCache() ?? DEFAULT_SETTINGS,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await adminSettingsService.getAll();
      setSettings(payload);
      writeCache(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!authLoading && isAdmin) void refresh();
  }, [authLoading, isAdmin, refresh]);

  // Multi-tab sync — storage events fire in other tabs only.
  useEffect(() => {
    if (!settings.productivity.multiTabSync) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== LOCAL_KEY || !e.newValue) return;
      try {
        setSettings(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [settings.productivity.multiTabSync]);

  const updateCategory = useCallback(
    async <C extends AdminSettingsCategory>(
      category: C,
      values: Partial<AdminSettingsPayload[C]>,
    ) => {
      setSettings((prev) => {
        const next = {
          ...prev,
          [category]: { ...prev[category], ...values },
        } as AdminSettingsPayload;
        writeCache(next);
        return next;
      });
      // Debounce per category so sliders/colour pickers don't spam the API.
      if (debounceRef.current[category]) {
        clearTimeout(debounceRef.current[category]);
      }
      debounceRef.current[category] = setTimeout(async () => {
        setSaving(true);
        setError(null);
        try {
          const saved = await adminSettingsService.updateCategory(category, values);
          setSettings((prev) => {
            const next = { ...prev, [category]: saved } as AdminSettingsPayload;
            writeCache(next);
            return next;
          });
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to save');
        } finally {
          setSaving(false);
        }
      }, 400);
    },
    [],
  );

  const resetCategory = useCallback(
    async <C extends AdminSettingsCategory>(category: C) => {
      setSaving(true);
      try {
        const saved = await adminSettingsService.resetCategory(category);
        setSettings((prev) => {
          const next = { ...prev, [category]: saved } as AdminSettingsPayload;
          writeCache(next);
          return next;
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to reset');
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const resetAll = useCallback(async () => {
    setSaving(true);
    try {
      const payload = await adminSettingsService.resetAll();
      setSettings(payload);
      writeCache(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset');
    } finally {
      setSaving(false);
    }
  }, []);

  const value = useMemo<SettingsContextValue>(
    () => ({ settings, loading, saving, error, updateCategory, resetCategory, resetAll, refresh }),
    [settings, loading, saving, error, updateCategory, resetCategory, resetAll, refresh],
  );

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
}
